import type { ReactNode } from 'react';

import { Box } from '@mui/material';

interface DrawerLayoutProps {
  /** Chrome header (title bar with back/close). Pinned to the very top. */
  header?: ReactNode;
  /**
   * In-content header (title / subtitle / filters). Pinned directly below the
   * chrome header and never scrolls — it is a sibling of, not inside, the
   * scroll region, so it stays put regardless of how tall the body grows.
   */
  subheader?: ReactNode;
  /** Scrollable body content. */
  children: ReactNode;
  /** Footer actions. Pinned to the bottom. */
  footer?: ReactNode;
}

/**
 * Shared layout for the contents of a side drawer: a pinned chrome header, an
 * optional pinned in-content header, a scrolling body, and a pinned footer.
 *
 * Centralises the "pinned header / scrolling body / pinned footer" arrangement
 * so individual drawers (dashboard app config, data export, …) don't each
 * reimplement it.
 */
export const DrawerLayout = ({ header, subheader, children, footer }: DrawerLayoutProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
    {header != null && <Box sx={{ flexShrink: 0 }}>{header}</Box>}
    {subheader != null && <Box sx={{ flexShrink: 0, px: 3, pt: 3, pb: 3 }}>{subheader}</Box>}
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        px: 3,
        pb: 3,
        pt: subheader == null ? 3 : 0
      }}
    >
      {children}
    </Box>
    {footer != null && <Box sx={{ flexShrink: 0 }}>{footer}</Box>}
  </Box>
);
